import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
// @ts-ignore - Vite asset handled via ?url
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';

import { auth } from '../lib/firebase';
import { createApiError } from '../lib/usageLimitMessages';

export async function renderPdfPageToImage(pdfData: string, pageNumber: number): Promise<string> {
    try {
        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
        }

        const binaryData = atob(pdfData);
        const uint8Array = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
            uint8Array[i] = binaryData.charCodeAt(i);
        }

        const loadingTask = pdfjsLib.getDocument({ data: uint8Array });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(pageNumber);
        
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        if (!context) throw new Error('Could not get canvas context');
        
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        await page.render({
            canvasContext: context,
            viewport: viewport,
            canvas: canvas
        }).promise;
        
        return canvas.toDataURL('image/png').split(',')[1];
    } catch (err) {
        console.error('Failed to render PDF page to image:', err);
        return '';
    }
}
import { safeStringify, isMobileDevice } from '../lib/utils';
import { 
    CourseStructure, 
    CourseContent, 
    LevelContent,
    QuizQuestion,
    ProgressReport, 
    DocumentChunk,
    LessonDepth,
    LessonStructure
} from '../types';

export type {
    CourseStructure, 
    CourseContent, 
    LevelContent,
    QuizQuestion,
    ProgressReport, 
    DocumentChunk,
    LessonDepth,
    LessonStructure
};

// Configure pdfjs worker to use the locally bundled version from node_modules
// This ensures PWA/Mobile reliability without relying on external CDNs
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

/**
 * PDF Processing Profiles
 */
interface PDFProcessingProfile {
  name: 'desktop' | 'mobile';
  renderScale: number;
  aggressiveCleanup: boolean;
  maxFileSizeMB: number;
  maxPages: number;
}

const DESKTOP_PROFILE: PDFProcessingProfile = {
  name: 'desktop',
  renderScale: 2.0,
  aggressiveCleanup: false,
  maxFileSizeMB: 100,
  maxPages: 500
};

const MOBILE_PROFILE: PDFProcessingProfile = {
  name: 'mobile',
  renderScale: 1.2,
  aggressiveCleanup: true,
  maxFileSizeMB: 20,
  maxPages: 60
};

export function getProcessingProfile(): PDFProcessingProfile {
  return isMobileDevice() ? MOBILE_PROFILE : DESKTOP_PROFILE;
}

export function shouldPreferBackendProcessing(files: { name: string; data: string; mimeType: string }[]): boolean {
  // In pilot mode with Cloud Storage, the frontend no longer holds base64 data, so we MUST use backend
  return true;
}

/**
 * THIN CLIENT: All AI logic moved to server/aiRuntime.ts
 */

export async function createBackgroundJob(
    title: string,
    sources: any[],
    userId: string,
    courseId?: string,
    intent?: string,
    documentId?: string,
    options: {
        description?: string;
        visibility?: 'private' | 'organisation' | 'public';
        progressionMode?: 'flexible' | 'locked';
        sourceSetId?: string | null;
        organisationId?: string | null;
    } = {}
) {
    return await callServerApi('/api/process-documents', {
        title,
        sources,
        userId,
        courseId,
        intent,
        documentId,
        ...options
    });
}

async function callServerApi(endpoint: string, data: any, retries = 3, onProgress?: (msg: string) => void) {
    let lastError: any;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            
            const hasExplicitOrganisationId = data && Object.prototype.hasOwnProperty.call(data, 'organisationId');
            const explicitOrganisationId = typeof data?.organisationId === 'string' ? data.organisationId.trim() : '';
            const fallbackOrganisationId = !hasExplicitOrganisationId ? (localStorage.getItem('last_org_id') || '') : '';
            const scopedOrganisationId = explicitOrganisationId || fallbackOrganisationId;

            if (scopedOrganisationId) {
                headers['X-Organization-Id'] = scopedOrganisationId;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: safeStringify(data)
            });
            
            const contentType = response.headers.get("content-type");
            const isJson = contentType && contentType.indexOf("application/json") !== -1;
            
            if (!response.ok) {
                if (isJson) {
                    const err = await response.json();
                    throw createApiError(response, err, `API error (${response.status})`);
                } else {
                    const text = await response.text();
                    throw new Error(`Server error (${response.status}): ${text.slice(0, 100)}`);
                }
            }
            
            if (!isJson) {
                throw new Error(`Unexpected non-JSON response from server (${response.status})`);
            }
            
            const envelope = await response.json();
            if (!envelope.success) {
                throw createApiError(response, envelope, 'Unknown API error');
            }
            
            return envelope.data;
        } catch (err: any) {
            lastError = err;
            const isNetworkError = err.message === 'Failed to fetch' || err.name === 'TypeError';
            
            if (isNetworkError && attempt < retries) {
                console.warn(`[Gemini Service] API call to ${endpoint} failed (Attempt ${attempt + 1}/${retries + 1}). Retrying in ${Math.pow(2, attempt) * 1000}ms...`);
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
                continue;
            }
            break; // Not a network error or no retries left
        }
    }

    console.error(`[Gemini Service] API call to ${endpoint} failed after ${retries + 1} attempts:`, lastError);
    throw lastError;
}

/**
 * Security Gate: Block direct browser SDK usage
 */
export function getAI() {
    throw new Error("CLIENT_SIDE_AI_CALL_BLOCKED");
}

let connectionCache: { success: boolean; message: string } | null = null;
let connectionPromise: Promise<{ success: boolean; message: string }> | null = null;

export async function testGeminiConnection(): Promise<{ success: boolean; message: string }> {
    if (connectionCache) {
        console.log("Using cached AI connection result");
        return connectionCache;
    }
    if (connectionPromise) return connectionPromise;

    connectionPromise = callServerApi('/api/test-connection', {})
        .then(result => {
            connectionCache = result;
            return result;
        })
        .catch(err => {
            connectionPromise = null;
            throw err;
        });
        
    return connectionPromise;
}

async function performOCR(base64Image: string): Promise<string> {
    const res = await callServerApi('/api/ocr', { image: base64Image });
    return res.text;
}

async function renderPageToImage(page: any, scale: number = 2.0): Promise<string> {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
  
  if (!context) throw new Error("Could not create canvas context");
  
  canvas.height = viewport.height;
  canvas.width = viewport.width;

  const renderContext = {
    canvasContext: context,
    viewport: viewport,
    enableWebGL: false // Stick to 2D for memory stability on mobile
  };

  try {
    const renderTask = page.render(renderContext);
    await renderTask.promise;
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8); // JPEG is smaller than PNG
    
    // Immediate canvas cleanup
    canvas.width = 0;
    canvas.height = 0;
    
    return dataUrl;
  } catch (err) {
    console.error("[Renderer] Page render failed:", err);
    throw err;
  }
}

/**
 * Helper for timing out async operations
 */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    let timeoutId: any;
    const timeoutPromise = new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('TIMEOUT')), timeoutMs);
    });
    return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
}

export async function extractTextFromPDF(
  base64Data: string, 
  onProgress?: (msg: string) => void,
  docIndex: number = 1,
  totalDocs: number = 1
): Promise<{ text: string; pagesProcessed: number; pagesSkipped: number; totalPages: number }> {
    const profile = getProcessingProfile();
    const isMobile = profile.name === 'mobile';
    
    if (isMobile) {
        onProgress?.("Entering Mobile Safe Low-Memory Mode...");
    }

    const startTime = Date.now();
    const binaryData = atob(base64Data);
    const uint8Array = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
    }
    
    let pdf;
    try {
        const loadingTask = pdfjsLib.getDocument({ 
            data: uint8Array,
            // Disable dynamic fetches from CDN for PWA reliability
            isEvalSupported: false,
            // Use local font loading
            disableFontFace: false
        });
        
        // Add a primary timeout for the initial load which often hangs if worker fails
        pdf = await withTimeout(loadingTask.promise, 15000); 
    } catch (err: any) {
        console.error("[OCR] Failed to initialize PDF worker:", err);
        throw new Error(
            "PDF processing engine failed to start. This is usually due to offline mode or browser memory limits. " +
            "Please try refreshing the page or using a smaller document."
        );
    }
    
    let fullText = "";
    let pagesProcessed = 0;
    let pagesSkipped = 0;
    const totalPages = pdf.numPages;
    const PAGE_TIMEOUT = 25000; // 25 seconds per page
    const DOC_TIMEOUT = 180000; // 180 seconds per document
    const MAX_FAILURE_RATE = 0.5; // 50%

    console.log(`[OCR] Starting Document ${docIndex}/${totalDocs} (${totalPages} pages)`);

    for (let i = 1; i <= totalPages; i++) {
        // Global document timeout check
        if (Date.now() - startTime > DOC_TIMEOUT) {
            console.warn(`[OCR] Document ${docIndex} timed out after ${DOC_TIMEOUT}ms. Skipping remaining pages.`);
            fullText += `\n\n[DOCUMENT TIMEOUT] Extraction stopped due to time limit.`;
            pagesSkipped += (totalPages - i + 1);
            break;
        }

        // Fail-safe escape check
        if (pagesSkipped > totalPages * MAX_FAILURE_RATE && totalPages > 5) {
            console.error(`[OCR] Document ${docIndex} failure rate exceeded 30%. Aborting OCR.`);
            fullText += `\n\n[OCR FAILURE] Extraction aborted due to high failure rate.`;
            pagesSkipped += (totalPages - i + 1);
            break;
        }

        onProgress?.(`Extracting text: Page ${i} of ${totalPages} (Document ${docIndex} of ${totalDocs})`);
        
        let pageText = "";
        let pageSuccess = false;
        let attempt = 0;
        const maxAttempts = 3;

        while (!pageSuccess && attempt < maxAttempts) {
            attempt++;
            try {
                // Try simple text extraction first
                const page = await withTimeout((pdf as any).getPage(i), PAGE_TIMEOUT) as any;
                const textContent = await withTimeout(page.getTextContent(), PAGE_TIMEOUT) as any;
                const extractedText = textContent.items
                  .map((item: any) => item.str)
                  .join(' ')
                  .trim();

                if (extractedText.length < 150) {
                    // Fallback to OCR (render + AI OCR)
                    const imageData = await withTimeout(renderPageToImage(page, profile.renderScale), PAGE_TIMEOUT);
                    const ocrText = await withTimeout(performOCR(imageData), PAGE_TIMEOUT);
                    pageText = ocrText;
                } else {
                    pageText = extractedText;
                }
                
                fullText += `\n\n[PAGE ${i}]\n${pageText}`;
                pageSuccess = true;
                pagesProcessed++;

                // AGGRESSIVE CLEANUP
                if (page.cleanup) page.cleanup();
            } catch (err: any) {
                console.warn(`[OCR] Page ${i} attempt ${attempt} failed: ${err.message}`);
                if (attempt >= maxAttempts) {
                    pagesSkipped++;
                    fullText += `\n\n[PAGE ${i} (FAILED)]\nExtraction failed after ${maxAttempts} attempts. Content skipped.`;
                } else {
                    // Quick sleep before retry
                    await new Promise(r => setTimeout(r, 500));
                }
            }
        }
    }

    const duration = ((Date.now() - startTime) / 1000).toFixed(1);
    console.log(`[OCR] Document ${docIndex} complete: ${pagesProcessed} processed, ${pagesSkipped} skipped in ${duration}s`);
    
    // Final destroy of the PDF proxy to free worker resources
    if (pdf && (pdf as any).destroy) {
        (pdf as any).destroy();
    }
    
    return { text: fullText, pagesProcessed, pagesSkipped, totalPages };
}

export async function generateCourseFromInputs(
  title: string,
  pdfs: { name: string; data: string; mimeType: string }[],
  urls: string[],
  onProgress?: (report: ProgressReport) => void,
  jobId?: string
): Promise<CourseStructure> {
    const profile = getProcessingProfile();
    
    // Mobile Mode Safeguards
    const totalPdfsSizeMB = pdfs.reduce((acc, pdf) => {
        const base64Size = pdf.data.length - (pdf.data.indexOf(',') + 1);
        const padding = (pdf.data.charAt(pdf.data.length - 2) === '=') ? 2 : ((pdf.data.charAt(pdf.data.length - 1) === '=') ? 1 : 0);
        return acc + ((base64Size * 0.75 - padding) / (1024 * 1024));
    }, 0);

    if (profile.name === 'mobile') {
        if (totalPdfsSizeMB > profile.maxFileSizeMB) {
            throw new Error(`This file is too heavy for full mobile processing. (${totalPdfsSizeMB.toFixed(1)}MB > ${profile.maxFileSizeMB}MB limit). Please try on desktop or upload a smaller file.`);
        }
    }

    onProgress?.({ 
      stage: "uploading", 
      progress_percent: 5, 
      message: "Uploading and preparing documents..." 
    });
    
    // We now send the raw PDF data to the server for Multimodal processing (much faster)
    // For large documents that might need explicit OCR on the client-side (legacy fallback), 
    // we still keep the extraction logic but prefer multimodal.
    
    onProgress?.({ 
      stage: "ocr", 
      progress_percent: 20, 
      message: "Analyzing layouts and extracting text...",
      details: pdfs.length > 0 ? `Processing ${pdfs.length} PDF(s)` : "Processing source URLs"
    });
    
    try {
        // Sequentially process PDFs to avoid OOM or hanging
        const processedPdfs = [];
        for (let i = 0; i < pdfs.length; i++) {
            const pdf = pdfs[i];
            
            const isWordLike = pdf.mimeType?.includes('word') || pdf.mimeType?.includes('officedocument') || (pdf.name || '').toLowerCase().endsWith('.docx') || (pdf.name || '').toLowerCase().endsWith('.doc');
            
            if (isWordLike) {
                processedPdfs.push({
                    ...pdf,
                    content: "", // Will be extracted on the backend
                    data: (pdf.data || "").replace(/^data:[^;]+;base64,/, ""),
                    pagesProcessed: 1,
                    pagesSkipped: 0,
                    totalPages: 1
                });
                continue;
            }

            const { text, pagesProcessed, pagesSkipped, totalPages } = await extractTextFromPDF(
                pdf.data, 
                (msg) => onProgress?.({ 
                    stage: "ocr", 
                    progress_percent: 20 + Math.floor((i / pdfs.length) * 30), 
                    message: msg,
                    details: `Document ${i+1}/${pdfs.length}: ${pdf.name}`
                }),
                i + 1,
                pdfs.length
            );
            
            processedPdfs.push({
                ...pdf,
                content: text, // Send extracted text to server
                data: (pdf.data || "").replace(/^data:application\/pdf;base64,/, ""), // Still send binary for visual analysis if needed
                pagesProcessed,
                pagesSkipped,
                totalPages
            });
        }

        const fetchStructure = async () => {
            return await callServerApi('/api/generate-course', { 
                title, 
                pdfs: processedPdfs,
                urls,
                jobId
            });
        };

        let unifiedStructure;
        try {
            unifiedStructure = await fetchStructure();
        } catch (initialErr: any) {
            console.warn(`[Gemini Service] First pass threw an error: ${initialErr.message}. Automatically retrying...`);
            unifiedStructure = null;
        }
        
        // Completion Check: Is the first pass blank?
        const isBlank = (s: CourseStructure | null) => {
            if (!s) return true;
            const explorerLessons = s.levels?.explorer?.lessons || s.explorer?.lessons || [];
            const apprenticeLessons = s.levels?.apprentice?.lessons || s.apprentice?.lessons || [];
            const masterLessons = s.levels?.master?.lessons || s.master?.lessons || [];
            
            const lessonsCount = explorerLessons.length + apprenticeLessons.length + masterLessons.length;
            return lessonsCount === 0;
        };

        console.log("[Gemini Service] First pass result validation check...");
        if (isBlank(unifiedStructure)) {
            console.warn("[Gemini Service] First pass result was EMPTY or failed. Automatically retrying...");
            
            onProgress?.({ 
              stage: "generation", 
              progress_percent: 85, 
              message: "Finalising course generation...",
              details: "Optimizing knowledge structure..."
            });

            // Automatic single retry
            unifiedStructure = await fetchStructure();
            
            if (isBlank(unifiedStructure)) {
                console.error("[Gemini Service] Second generation pass also returned EMPTY results.");
                throw new Error("COURSE_EMPTY_AFTER_RETRY: The AI could not extract a meaningful course structure from this source after multiple attempts. The PDF might contain primarily images without clear textual content.");
            }
            console.log("[Gemini Service] Retry successful. Course data retrieved.");
        } else {
            console.log("[Gemini Service] First pass successful. Data validated.");
        }
        
        onProgress?.({ 
          stage: "content_processing", 
          progress_percent: 50, 
          message: "Structuring knowledge map..." 
        });

        onProgress?.({ 
          stage: "generation", 
          progress_percent: 80, 
          message: "Generating interactive lessons..." 
        });

        onProgress?.({ 
          stage: "finalizing", 
          progress_percent: 95, 
          message: "Finalizing course layout..." 
        });
        
        return unifiedStructure;
    } catch (err: any) {
        console.error("Course generation failed:", err);
        throw err;
    }
}

export async function generateLessonDepths(lesson: LessonStructure): Promise<LessonDepth> {
    return await callServerApi('/api/generate-lesson', { lessonStructure: lesson });
}

export async function generateQuizForLesson(lessonContent: any, visuals?: any[]): Promise<QuizQuestion[]> {
    return await callServerApi('/api/generate-quiz', { lessonContent, visuals });
}

export async function generateReviewQuiz(concepts: any[]): Promise<QuizQuestion[]> {
    return await callServerApi('/api/generate-review-quiz', { concepts });
}

export async function tutorChat(history: any[], message: string, context?: any): Promise<string> {
    const res = await callServerApi('/api/tutor', { history, message, context });
    return res.response;
}
